import { services } from '@config/services';
import { vendorApi } from './_base';
import type { VendorProductRow } from './vendorProducts';

/**
 * Merchant overrides over a vendor's product catalog
 * (`/v1/merchantVendorProducts*`). Rows are shaped identically to
 * `VendorProductRow` but represent what the merchant has CUSTOMIZED
 * on top of the vendor's defaults — empty array means the merchant
 * is using the vendor's defaults verbatim.
 *
 * Bulk endpoints serve the CSV-upload + revert-to-default flows.
 */

/** Wrapper for merchant overrides; rows have the same shape as
 * VendorProductRow but represent per-merchant customizations on top of
 * the vendor's defaults. */
export interface MerchantVendorProductsResponse {
  merchantVendorProducts: VendorProductRow[];
  total: number;
}

export interface MerchantVendorProductsArgs {
  merchantId: string;
  vendorId: string;
  page?: number;
  pageSize?: number;
  /** Free-text term, serialized as `searchAll` (same semantics as the vendor
   * catalog search). Omitted when blank so existing cache keys don't churn. */
  searchString?: string | null;
  /** Facility ids to filter by (repeated `facilityIds` params). */
  facilityIds?: string[];
}

/** Bulk create body — mirrors portalnew's `{ bulk: [...] }` envelope. */
export interface MerchantVendorProductsBulkBody {
  bulk: VendorProductRow[];
}

/** Per-row outcome from the bulk endpoint. Some rows may 200 while others
 * 400 with errors (e.g. invalid facility id). */
export interface MerchantVendorProductsBulkResponse {
  bulk: Array<{
    status: number;
    response?: { errors?: Array<{ errorMessage?: string }>; id?: string };
  }>;
}

/** Response from the delete-all endpoint — how many overrides were removed. */
export interface DeleteAllMerchantVendorProductsResponse {
  deletedCount: number;
}

/**
 * The one place the `GET /v1/merchantVendorProducts` wire contract is built —
 * also used by the simulator's overrides probe + linked-tier sample
 * (`vendorProductsForVendors.ts`), so a server-side param change lands once.
 */
export function merchantVendorProductsUrl({
  merchantId,
  vendorId,
  page = 1,
  pageSize = 100,
  searchString,
  facilityIds,
}: MerchantVendorProductsArgs): string {
  const qs = new URLSearchParams();
  qs.set('merchantId', merchantId);
  qs.set('vendorId', vendorId);
  qs.set('page', String(page));
  qs.set('pageSize', String(pageSize));
  if (searchString?.trim()) qs.set('searchAll', searchString.trim());
  for (const id of facilityIds ?? []) qs.append('facilityIds', id);
  return `${services.vendor}/v1/merchantVendorProducts?${qs.toString()}`;
}

const merchantVendorProductsApi = vendorApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /vendor/v1/merchantVendorProducts?merchantId=&vendorId=&page=&pageSize=
     *
     * Per-merchant overrides of a vendor's product mappings. The
     * `merchantVendorProducts` array carries rows shaped identically to
     * `vendorProducts` (variantId, sku, facilities[{vendorSku, productCost,
     * decorations}]) but represents what the merchant has CUSTOMIZED on top
     * of the vendor's defaults. Empty array → using vendor defaults.
     */
    getMerchantVendorProducts: builder.query<
      MerchantVendorProductsResponse,
      MerchantVendorProductsArgs
    >({
      query: (args) => ({ url: merchantVendorProductsUrl(args), method: 'GET' }),
      providesTags: (_r, _e, { merchantId, vendorId }) => [
        { type: 'Vendor', id: `mvp:${merchantId}:${vendorId}` },
      ],
    }),
    /** POST /vendor/v1/merchantVendorProducts/bulk/optimized — create the
     * merchant's product-mapping overrides via the single-batch fast path
     * (validates the vendor connection once, inserts in one write). Body shape:
     * `{ bulk: VendorProductRow[] }`. */
    bulkCreateMerchantVendorProducts: builder.mutation<
      MerchantVendorProductsBulkResponse,
      { merchantId: string; vendorId: string; body: MerchantVendorProductsBulkBody }
    >({
      query: ({ body }) => ({
        url: `${services.vendor}/v1/merchantVendorProducts/bulk/optimized`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { merchantId, vendorId }) => [
        { type: 'Vendor', id: `mvp:${merchantId}:${vendorId}` },
      ],
    }),
    /**
     * DELETE /vendor/v1/merchantVendorProducts/bulk — delete SELECTED override
     * records by id. Body shape: `{ bulk: string[] }`. The response carries a
     * per-id outcome (some ids may 200 while others fail), mirroring the bulk
     * create envelope.
     */
    bulkDeleteMerchantVendorProducts: builder.mutation<
      MerchantVendorProductsBulkResponse,
      { merchantId: string; vendorId: string; ids: string[] }
    >({
      query: ({ ids }) => ({
        url: `${services.vendor}/v1/merchantVendorProducts/bulk`,
        method: 'DELETE',
        body: { bulk: ids },
      }),
      invalidatesTags: (_r, _e, { merchantId, vendorId }) => [
        { type: 'Vendor', id: `mvp:${merchantId}:${vendorId}` },
      ],
    }),
    /**
     * DELETE /vendor/v1/merchantVendorProducts/all — revert ALL of a merchant's
     * overrides for one vendor back to the vendor defaults in a single call.
     * Body shape: `{ merchantId, vendorId }`. The service deletes by criteria,
     * so the client no longer fetches every override id across pages just to
     * delete them in batches.
     */
    deleteAllMerchantVendorProducts: builder.mutation<
      DeleteAllMerchantVendorProductsResponse,
      { merchantId: string; vendorId: string }
    >({
      query: ({ merchantId, vendorId }) => ({
        url: `${services.vendor}/v1/merchantVendorProducts/all`,
        method: 'DELETE',
        body: { merchantId, vendorId },
      }),
      invalidatesTags: (_r, _e, { merchantId, vendorId }) => [
        { type: 'Vendor', id: `mvp:${merchantId}:${vendorId}` },
      ],
    }),
  }),
});

export const {
  useGetMerchantVendorProductsQuery,
  useLazyGetMerchantVendorProductsQuery,
  useBulkCreateMerchantVendorProductsMutation,
  useBulkDeleteMerchantVendorProductsMutation,
  useDeleteAllMerchantVendorProductsMutation,
} = merchantVendorProductsApi;
